/**
 * useAutoRebuildOnSave — Phase B (E.21.37)
 *
 * Subscribes to projectStore's `vibe:file:save:start | saved | save:error`
 * lifecycle events and:
 *   - drives toast progression (Saving → Saved / Save failed)
 *   - if configStore.autoRebuildOnSave === true, debounces 500ms and
 *     fires buildStore.startBuild + pollBuildStatus, then calls
 *     buildStore.refreshPreview() (Reviewer 1 Concern 3 — reuse the
 *     existing cache-buster mechanism rather than duplicating it)
 *   - drives Rebuilding / Preview updated / Build failed toasts
 *
 * Race-safe: when configStore.autoRebuildOnSave is null (not yet fetched),
 * NO rebuild is triggered. A Cmd+S in the 50ms before /api/config returns
 * does not bypass per-tenant config.
 *
 * Coupling: this hook is the integration point for Phase B. It does NOT
 * import projectStore (events flow via window). It DOES read configStore
 * + buildStore via getState/subscribe — both are zustand patterns that
 * work outside React.
 *
 * Toast lifetimes — 1.5s for transient ("Saving"/"Saved"), 60s for
 * "Rebuilding…" (manually dismissed on terminal status), 2s for "Preview
 * updated", 4s for error toasts.
 */
import { useEffect, useRef } from 'react';
import { useToast } from '../components/useToast';
import { useConfigStore } from '../stores/configStore';
import { useBuildStore } from '../stores/buildStore';

const DEBOUNCE_MS = 500;

interface SavedEventDetail {
  projectId: string;
  fileId: string;
  path: string;
}

interface SaveErrorEventDetail extends SavedEventDetail {
  error: string;
}

export function useAutoRebuildOnSave(): void {
  const toast = useToast();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Capture toast in a ref so the event-listener closures stay stable but
  // always call the latest toast methods (useToast returns a fresh object
  // on every render). Using the ref avoids re-binding listeners on every
  // render which would clobber pending timers.
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    const onSaveStart = () => {
      toastRef.current.info('Saving…', 1500);
    };

    const onSaved = (e: Event) => {
      const detail = (e as CustomEvent<SavedEventDetail>).detail;
      toastRef.current.success('Saved', 1500);

      // Gate on the LATEST autoRebuildOnSave value at event time, NOT a
      // closure over the value at hook-mount time. Read via getState() so
      // a config refresh that flips the flag takes effect on the next
      // event without re-mounting the hook.
      const autoRebuildOnSave = useConfigStore.getState().autoRebuildOnSave;
      if (autoRebuildOnSave !== true) {
        // Includes false (opt-out) AND null (race-safe default before
        // /api/config has resolved). Documented in the test suite.
        return;
      }
      if (!detail?.projectId) return;

      // Debounce: clear any pending timer + start a fresh 500ms window.
      // A burst of N saves in 500ms coalesces to 1 rebuild.
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void triggerRebuild(detail.projectId);
      }, DEBOUNCE_MS);
    };

    const onSaveError = (e: Event) => {
      const detail = (e as CustomEvent<SaveErrorEventDetail>).detail;
      toastRef.current.error(`Save failed: ${detail?.error ?? 'unknown error'}`, 4000);
    };

    const triggerRebuild = async (projectId: string) => {
      // Reviewer 2 Concern 1 fix: addToast now returns the new toast's
      // ID, so dismiss(rebuildingToastId) actually removes the toast
      // when terminal status arrives (pre-fix the cast-from-void was a
      // silent no-op and the toast hung for the full 60s).
      const rebuildingToastId = toastRef.current.info('Rebuilding…', 60000);
      const { startBuild, pollBuildStatus, refreshPreview } = useBuildStore.getState();
      try {
        await startBuild(projectId);
        await pollBuildStatus(projectId);
      } catch (err) {
        toastRef.current.dismiss(rebuildingToastId);
        toastRef.current.error(`Build failed: ${String(err)}`, 4000);
        return;
      }
      const finalStatus = useBuildStore.getState().buildStatus.status;
      toastRef.current.dismiss(rebuildingToastId);
      if (finalStatus === 'success') {
        refreshPreview();
        toastRef.current.success('Preview updated', 2000);
      } else if (finalStatus === 'error') {
        toastRef.current.error('Build failed', 4000);
      }
      // 'cancelled' is handled by cancelBuild's own toasts (E.21.34) —
      // intentional silent here.
    };

    window.addEventListener('vibe:file:save:start', onSaveStart);
    window.addEventListener('vibe:file:saved', onSaved);
    window.addEventListener('vibe:file:save:error', onSaveError);
    return () => {
      window.removeEventListener('vibe:file:save:start', onSaveStart);
      window.removeEventListener('vibe:file:saved', onSaved);
      window.removeEventListener('vibe:file:save:error', onSaveError);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // Empty deps: listeners are bound ONCE per mount; toast updates flow
    // through toastRef.current. autoRebuildOnSave is read via getState()
    // at event time (latest value).
  }, []);
}

export default useAutoRebuildOnSave;
